import {
  Box,
  Typography
} from "@mui/material";
import { Theme } from "@mui/material/styles";
import dayjs from "dayjs";
import type { TFunction } from "i18next";
import clockIcon from "../../assets/clock-2.svg";
import graduationCapIcon from "../../assets/graduation-cap.svg";
import mapPinned from "../../assets/map-pinned.svg";
import videoIcon from "../../assets/video.svg";
import { formatDateWithCapitalizedMonth } from "../../queries";
import { SessionWithTeachersAndTutors } from "../../types.d";
interface SessionItemProps {
  index: number;
  totalSessions: number;
  session: SessionWithTeachersAndTutors;
  isVirtual: boolean;
  theme: Theme;
  t: TFunction;
}

export function SessionItem({ index, totalSessions, session, isVirtual, theme, t }: SessionItemProps) {
  const hasTeachersOrTutors = (session.teachers && session.teachers.length > 0) || (session.tutors && session.tutors.length > 0);

  const formatSessionDateTime = (startDate: string, endDate: string): string => {
    const start = dayjs(new Date(startDate));
    const end = dayjs(new Date(endDate));
    return `${formatDateWithCapitalizedMonth(start)} | ${start.format("HH:mm")} - ${end.format("HH:mm")}`;
  };

  const getLocationText = (
    isVirtual: boolean,
    location: string | undefined,
    rooms: string[] | undefined,
    t: TFunction
  ): string => {
    if (isVirtual) {
      return t("virtual_location");
    }
    const locationText = location || t("physical_location");
    const roomsText = rooms && rooms.length > 0 ? rooms.join(", ") : "";
    return roomsText ? `${locationText}\n${roomsText}` : locationText;
  };
  

  return (
    <Box sx={ { pb: index !== totalSessions ? 1 : 0, pt: 2, px: "0.5rem" } }>
      <Box sx={ { alignItems: "center", display: "flex", mb: 0.5 } }>
        <img
          alt="clock"
          src={ clockIcon }
          width="16px"
          height="16px"
          style={ { filter: "brightness(0) invert(1)", marginRight: "8px" } }
        />
        <Typography variant="body2" fontWeight={ 700 } sx={ { color: theme.customColors.textPrimary } }>
          { formatSessionDateTime(session.startDate, session.endDate) }
        </Typography>
      </Box>
      
      <Box sx={ { alignItems: "center", display: "flex" } }>
        <img
          alt="location-icon"
          src={ isVirtual ? videoIcon : mapPinned }
          width="16px"
          height="16px"
          style={ { filter: "brightness(0) invert(1)", flexShrink: 0, marginRight: "8px", marginTop: "2px" } }
        />
        <Typography variant="body2" sx={ { color: theme.customColors.textDisabled, fontSize: "0.75rem", whiteSpace: "pre-line" } }>
          { getLocationText(isVirtual, session.location, session.rooms, t) }
        </Typography>
      </Box>
      
      { hasTeachersOrTutors && (
        <Box sx={ { alignItems: "flex-start", display: "flex", flexWrap: "wrap", gap: 1, mt: 2 } }>
          { session.teachers?.map((teacher, idx) => (
            <Box key={ `teacher-${idx}` } sx={ { alignItems: "center", display: "flex", mr: 1 } }>
              <img
                alt="teacher-icon"
                src={ graduationCapIcon }
                width="16px"
                height="16px"
                style={ { filter: "brightness(0) invert(1)", marginRight: "8px" } }
              />
              <Typography variant="body2" sx={ { color: theme.customColors.textDisabled, fontSize: "0.75rem" } }>
                { teacher.completeName } ({ t("teacher_field").toLocaleLowerCase() })
              </Typography>
            </Box>
          )) }
          { session.tutors?.map((tutor, idx) => (
            <Box key={ `tutor-${idx}` } sx={ { alignItems: "center", display: "flex" } }>
              <img
                alt="tutor-icon"
                src={ graduationCapIcon }
                width="16px"
                height="16px"
                style={ { filter: "brightness(0) invert(1)", marginRight: "8px" } }
              />
              <Typography variant="body2" sx={ { color: theme.customColors.textDisabled, fontSize: "0.75rem" } }>
                { tutor.completeName } (tutor)
              </Typography>
            </Box>
          )) }
        </Box>
      ) }
    </Box>
  );
}
